export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
}

interface LogData {
  level: LogLevel
  message: string
  meta?: Record<string, any>
  timestamp: Date
  context?: string
}

class Logger {
  private static instance: Logger
  private minLevel: LogLevel = LogLevel.INFO
  private isDevelopment: boolean

  private constructor() {
    this.isDevelopment = process.env.NODE_ENV === 'development'
    this.minLevel = this.isDevelopment ? LogLevel.DEBUG : LogLevel.WARN
  }

  static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger()
    }
    return Logger.instance
  }

  setLevel(level: LogLevel): void {
    this.minLevel = level
  }

  private shouldLog(level: LogLevel): boolean {
    return level >= this.minLevel
  }

  private formatMessage(data: LogData): string {
    const timestamp = data.timestamp.toISOString()
    const levelName = LogLevel[data.level]
    const context = data.context ? `[${data.context}]` : ''
    const meta = data.meta ? ` ${JSON.stringify(data.meta)}` : ''

    return `[${timestamp}] ${levelName} ${context} ${data.message}${meta}`
  }

  private log(
    level: LogLevel,
    message: string,
    meta?: Record<string, any>,
    context?: string
  ): void {
    if (!this.shouldLog(level)) {
      return
    }

    const logData: LogData = {
      level,
      message,
      meta,
      timestamp: new Date(),
      context,
    }

    const formattedMessage = this.formatMessage(logData)

    if (this.isDevelopment) {
      switch (level) {
        case LogLevel.DEBUG:
          console.debug(formattedMessage)
          break
        case LogLevel.INFO:
          console.info(formattedMessage)
          break
        case LogLevel.WARN:
          console.warn(formattedMessage)
          break
        case LogLevel.ERROR:
          console.error(formattedMessage)
          break
      }
    }

    // 프로덕션에서는 원격 로깅 서비스로 전송 (추후 구현)
    if (!this.isDevelopment && level >= LogLevel.ERROR) {
      this.sendToRemoteLogger(logData)
    }
  }

  private sendToRemoteLogger(data: LogData): void {
    // 추후 원격 로깅 서비스 연동 시 구현
    // 예: Sentry, LogRocket, 자체 로깅 API 등
  }

  debug(message: string, meta?: Record<string, any>, context?: string): void {
    this.log(LogLevel.DEBUG, message, meta, context)
  }

  info(message: string, meta?: Record<string, any>, context?: string): void {
    this.log(LogLevel.INFO, message, meta, context)
  }

  warn(message: string, meta?: Record<string, any>, context?: string): void {
    this.log(LogLevel.WARN, message, meta, context)
  }

  error(message: string, meta?: Record<string, any>, context?: string): void {
    this.log(LogLevel.ERROR, message, meta, context)
  }

  // 특정 컨텍스트용 로거 생성
  createContextLogger(context: string) {
    return {
      debug: (message: string, meta?: Record<string, any>) =>
        this.debug(message, meta, context),
      info: (message: string, meta?: Record<string, any>) =>
        this.info(message, meta, context),
      warn: (message: string, meta?: Record<string, any>) =>
        this.warn(message, meta, context),
      error: (message: string, meta?: Record<string, any>) =>
        this.error(message, meta, context),
    }
  }
}

// 기본 로거 인스턴스 내보내기
export const logger = Logger.getInstance()

// 컨텍스트별 로거 생성 헬퍼
export const createLogger = (context: string) =>
  logger.createContextLogger(context)

// 빠른 사용을 위한 단축 함수들
export const logDebug = (
  message: string,
  meta?: Record<string, any>,
  context?: string
) => logger.debug(message, meta, context)

export const logInfo = (
  message: string,
  meta?: Record<string, any>,
  context?: string
) => logger.info(message, meta, context)

export const logWarn = (
  message: string,
  meta?: Record<string, any>,
  context?: string
) => logger.warn(message, meta, context)

export const logError = (
  message: string,
  meta?: Record<string, any>,
  context?: string
) => logger.error(message, meta, context)
